import { createContext, useContext, useEffect, useState } from 'react';
import { useClerk, useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';

interface AuthContextType {
  user: any;
  isLoaded: boolean;
  isSignedIn: boolean;
  signIn: () => void;
  signOut: () => void;
  signUp: () => void;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoaded, isSignedIn } = useUser();
  const { signIn, signOut, signUp } = useClerk();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Handle authentication state changes
  useEffect(() => {
    if (isLoaded) {
      setLoading(false);

      // Redirect logic based on auth state
      if (isSignedIn && window.location.pathname === '/sign-in') {
        navigate('/dashboard');
      } else if (!isSignedIn && window.location.pathname.startsWith('/app')) {
        navigate('/sign-in');
      }
    }
  }, [isLoaded, isSignedIn, navigate]);

  // Error handling
  const handleError = (err: unknown) => {
    console.error('Auth error:', err);
    setError(err instanceof Error ? err.message : 'Authentication error occurred');
    toast({
      title: 'Authentication Error',
      description: error || 'Please try again',
      variant: 'destructive',
    });
  };

  // Wrapped sign-in with error handling
  const handleSignIn = async () => {
    try {
      setLoading(true);
      await signIn();
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  // Wrapped sign-out with error handling
  const handleSignOut = async () => {
    try {
      setLoading(true);
      await signOut();
      navigate('/');
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  // Wrapped sign-up with error handling
  const handleSignUp = async () => {
    try {
      setLoading(true);
      await signUp();
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoaded,
        isSignedIn: isSignedIn || false,
        signIn: handleSignIn,
        signOut: handleSignOut,
        signUp: handleSignUp,
        loading,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};